<script setup>
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useBranding } from 'shared/composables/useBranding';
import { usePolicy } from 'dashboard/composables/usePolicy';
import { picoSearch } from '@scmmishra/pico-search';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useAlert } from 'dashboard/composables';
import IntegrationItem from './IntegrationItem.vue';
import { ordemDasIntegracoes } from './ordemDasIntegracoes';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();
const { replaceInstallationName } = useBranding();
const { isFeatureFlagEnabled } = usePolicy();

const searchQuery = ref('');
const uiFlags = getters['integrations/getUIFlags'];

// Google Agenda e Drive moram no Perfil (cada pessoa liga a própria conta).
// O cartão existe para quem procura "Google" em Integrações achar o caminho
// (Fase 2, parte 6 — 11/10/2026).
const cartaoDoGoogle = computed(() => ({
  id: 'google',
  name: t('INTEGRATION_APPS.GOOGLE.NOME'),
  description: t('INTEGRATION_APPS.GOOGLE.DESCRICAO'),
  enabled: false,
  icones: ['i-logos-google-calendar', 'i-logos-google-drive'],
  destino: frontendURL(
    `accounts/${getters.getCurrentAccountId.value}/profile/settings`
  ),
}));

// Quem precisa de atenção vem primeiro; depois o que é do escritório (T19).
const integrationList = computed(() => {
  const base = [
    ...getters['integrations/getAppIntegrations'].value,
    cartaoDoGoogle.value,
  ];
  if (isFeatureFlagEnabled(FEATURE_FLAGS.MENU_APPS)) {
    base.push({
      id: 'menu_apps',
      name: t('SIDEBAR.MENU_APPS'),
      description: t('INTEGRATION_SETTINGS.MENU_APPS.DESCRIPTION'),
      enabled: false,
      // Não há logo em /dashboard/images/integrations para este cartão: a
      // imagem saía quebrada. Ícone no lugar.
      icones: ['i-lucide-app-window'],
    });
  }
  return ordemDasIntegracoes(base);
});

const filteredIntegrationList = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return integrationList.value;
  return picoSearch(integrationList.value, query, ['name', 'description']);
});

onMounted(() => {
  // Falha ao carregar não pode virar lista vazia calada.
  store
    .dispatch('integrations/get')
    .catch(() => useAlert(t('INTEGRATION_SETTINGS.FALHA_AO_CARREGAR')));
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('INTEGRATION_SETTINGS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        icone="i-lucide-blocks"
        :title="$t('INTEGRATION_SETTINGS.HEADER')"
        :description="
          replaceInstallationName($t('INTEGRATION_SETTINGS.DESCRIPTION'))
        "
        :link-text="$t('INTEGRATION_SETTINGS.LEARN_MORE')"
        :search-placeholder="$t('INTEGRATION_SETTINGS.SEARCH_PLACEHOLDER')"
        feature-name="integrations"
      />
    </template>
    <template #body>
      <div class="flex-grow flex-shrink overflow-auto">
        <span
          v-if="!filteredIntegrationList.length && searchQuery"
          class="flex-1 flex items-center justify-center py-20 text-center text-body-main !text-base text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.NO_RESULTS') }}
        </span>
        <div
          v-else
          class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
        >
          <IntegrationItem
            v-for="item in filteredIntegrationList"
            :id="item.id"
            :key="item.id"
            :logo="item.logo"
            :name="item.name"
            :description="item.description"
            :enabled="item.enabled"
            :atencao="item.atencao"
            :icones="item.icones"
            :destino="item.destino"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
